import type { Cell } from '../model/types';

export interface HitCandidate {
  readonly id: string;
  readonly points: readonly Cell[];
}

/** Returns the shortest world-space distance from a point to a polyline. */
export function distanceToPolyline(point: Cell, points: readonly Cell[]): number {
  const first = points[0];
  if (first === undefined) {
    return Number.POSITIVE_INFINITY;
  }
  if (points.length === 1) {
    return Math.hypot(point.x - first.x, point.y - first.y);
  }

  let shortestDistance = Number.POSITIVE_INFINITY;
  for (let index = 1; index < points.length; index += 1) {
    const start = points[index - 1];
    const end = points[index];
    if (start === undefined || end === undefined) {
      continue;
    }

    const segmentX = end.x - start.x;
    const segmentY = end.y - start.y;
    const lengthSquared = segmentX * segmentX + segmentY * segmentY;
    const projection = lengthSquared === 0
      ? 0
      : ((point.x - start.x) * segmentX + (point.y - start.y) * segmentY) /
        lengthSquared;
    const clampedProjection = Math.max(0, Math.min(1, projection));
    const closestX = start.x + segmentX * clampedProjection;
    const closestY = start.y + segmentY * clampedProjection;
    shortestDistance = Math.min(
      shortestDistance,
      Math.hypot(point.x - closestX, point.y - closestY),
    );
  }

  return shortestDistance;
}

export function nearestCandidate(
  point: Cell,
  candidates: readonly HitCandidate[],
  maxDistance: number,
): string | null {
  let nearestId: string | null = null;
  let nearestDistance = Number.POSITIVE_INFINITY;

  candidates.forEach((candidate) => {
    const distance = distanceToPolyline(point, candidate.points);
    if (distance <= maxDistance && distance < nearestDistance) {
      nearestId = candidate.id;
      nearestDistance = distance;
    }
  });

  return nearestId;
}
